/**
 * Hexagonal Grid Utilities
 * 
 * Uses axial coordinate system (q, r) for hex positions
 * Converts between hex coordinates and pixel positions
 */

export interface HexCoord {
  q: number; // Column (axial)
  r: number; // Row (axial)
}

export interface PixelCoord {
  x: number;
  y: number;
}

/**
 * Hex grid configuration
 */
export const HEX_CONFIG = {
  size: 40, // Hex radius in pixels
  spacing: 1.0, // Spacing multiplier
  orientation: 'pointy' as const, // Pointy-top hexagons
};

/**
 * Convert hex coordinates (q, r) to pixel coordinates
 */
export function hexToPixel(hex: HexCoord, centerX: number, centerY: number): PixelCoord {
  const { size } = HEX_CONFIG;
  const x = size * (Math.sqrt(3) * hex.q + (Math.sqrt(3) / 2) * hex.r);
  const y = size * ((3 / 2) * hex.r);
  
  return {
    x: centerX + x,
    y: centerY + y,
  };
}

/**
 * Convert pixel coordinates to hex coordinates
 */
export function pixelToHex(pixel: PixelCoord, centerX: number, centerY: number): HexCoord {
  const { size } = HEX_CONFIG;
  const q = ((2 / 3) * (pixel.x - centerX)) / size;
  const r = ((-1 / 3) * (pixel.x - centerX) + (Math.sqrt(3) / 3) * (pixel.y - centerY)) / size;
  
  return hexRound({ q, r });
}

/**
 * Round fractional hex coordinates to nearest hex
 */
function hexRound(hex: HexCoord): HexCoord {
  let q = Math.round(hex.q);
  let r = Math.round(hex.r);
  const s = Math.round(-hex.q - hex.r);
  
  const qDiff = Math.abs(q - hex.q);
  const rDiff = Math.abs(r - hex.r);
  const sDiff = Math.abs(s - (-hex.q - hex.r));
  
  if (qDiff > rDiff && qDiff > sDiff) {
    q = -r - s;
  } else if (rDiff > sDiff) {
    r = -q - s;
  }
  
  return { q, r };
}

/**
 * Get all hex corners for rendering (pointy-top orientation)
 * Pointy-top hexagons have flat sides on top/bottom, pointy sides on left/right
 */
export function getHexCorners(center: PixelCoord, hexSize?: number): PixelCoord[] {
  const size = hexSize ?? HEX_CONFIG.size;
  const corners: PixelCoord[] = [];
  
  // For pointy-top hexagons, start at top-right corner (30 degrees from vertical)
  // Then go clockwise: right (90°), bottom-right (150°), bottom-left (210°), left (270°), top-left (330°)
  for (let i = 0; i < 6; i++) {
    // Offset by 30 degrees (π/6) to make it pointy-top
    const angle = (Math.PI / 3) * i + (Math.PI / 6);
    corners.push({
      x: center.x + size * Math.cos(angle),
      y: center.y + size * Math.sin(angle),
    });
  }
  
  return corners;
}

/**
 * Check if a point is inside a hexagon
 */
export function isPointInHex(point: PixelCoord, hexCenter: PixelCoord): boolean {
  const { size } = HEX_CONFIG;
  const dx = point.x - hexCenter.x;
  const dy = point.y - hexCenter.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  return distance <= size;
}

/**
 * Generate hex coordinates for Level 1 in a circular layout
 * Mimics lunar topography with a roughly circular map
 * Supports 2-4 players with ~37 hexes (radius 3-4)
 * 
 * Layout: Circular pattern with radius 3-4
 * - Center hex at (0, 0)
 * - Rings around center forming a circle
 * - More hexes than diagonal layout for better 4-player gameplay
 */
export function generateLevel1Hexes(): HexCoord[] {
  const hexes: HexCoord[] = [];
  const radius = 4; // Radius of the circular map
  
  // Generate all hexes within radius
  for (let q = -radius; q <= radius; q++) {
    const r1 = Math.max(-radius, -q - radius);
    const r2 = Math.min(radius, -q + radius);
    for (let r = r1; r <= r2; r++) {
      // Calculate distance from center (0, 0) using cube coordinates
      const s = -q - r;
      const distance = Math.max(Math.abs(q), Math.abs(r), Math.abs(s));
      
      // Include hex if within radius (circular boundary)
      if (distance <= radius) {
        // Create a more circular shape by excluding corner hexes
        // This makes it look more like lunar topography
        const cornerDistance = Math.sqrt(q * q + r * r + s * s);
        if (cornerDistance <= radius + 0.5) {
          hexes.push({ q, r });
        }
      }
    }
  }
  
  return hexes;
}

/**
 * Get hex coordinate from plot x, y (assuming backend uses similar coordinate system)
 */
export function plotToHex(plotX: number, plotY: number): HexCoord {
  // Backend might use different coordinate system, adjust as needed
  return { q: plotX, r: plotY };
}

/**
 * Get hex coordinate string for matching
 */
export function hexKey(hex: HexCoord): string {
  return `${hex.q},${hex.r}`;
}

